// Dependencies
import React from 'react';
import { useTranslation } from 'react-i18next';

// Styled Component
import { Container, Title, ImageIcon, Text, ActionsContainer } from './styles';

// Types
import { tagProps, variant } from './types';
import { tagTextColor } from './textColor';

export const SpecialTag = ({
  sizeTag = 'default',
  roid,
  variant,
  title,
  actions,
  ...props
}: tagProps): JSX.Element => {
  const { t } = useTranslation('common');

  const tag: { [key in variant]: string | undefined } = {
    campaign:
      sizeTag === 'default'
        ? `${t('orderTypes.campaign')}${title ? ':' : ''}`
        : `C${title ? ':' : ''}`,
    open: t('states.open'),
    managed: t('states.managed'),
    'pending-work': t('states.pending-work'),
    'work-in-progress': t('states.work-in-progress'),
    'in-progress': t('states.in-progress'),
    paused: t('states.paused'),
    'pending-validation': t('states.pending-validation'),
    'pending-at-validation': t('states.pending-at-validation'), // T66
    validated: t('states.validated'),
    'pending-certification': t('states.pending-certification'),
    partial: t('states.partial'),
    end: t('states.end'),
    annulled: t('states.annulled'),
    surveillanceRoute:
      sizeTag === 'default' ? t('orderTypes.surveillanceRoute') : 'RV',
    duplicate: t('states.duplicate'),
    roid: roid,
    default: undefined, // El título se renderiza en <Title> para evitar duplicación
    transfer: t('states.transfer'),
    transferred: t('states.transferred'),
    // P15 Task 1 — sin label i18n fijo: el texto (p.ej. "Su.NN"/"Tr.NN" o un
    // CTA) lo aporta el consumidor vía `title`, mismo mecanismo que `roid`
    // (que usa la prop `roid` en lugar de `title`). NO se renderizan vía
    // <Title> (ese span fuerza texto negro — mal contraste sobre
    // forestGreen/garnet); van directas por <Text> para heredar su color.
    linkedToSegment: title,
    linkedFromSegment: title,
    pendingLink: title,
    unlinkedWork: title,
    linkCta: title,
    // Etiqueta propia + ROID detrás (vía <Title>), igual que `campaign`. En
    // tablet se abrevia a «RV» para que quepa junto al chip de estado.
    fromRoute:
      sizeTag === 'default' ? t('orderTypes.surveillanceRoute') : 'RV',
  };

  return (
    <Container
      sizeTag={sizeTag}
      variant={variant}
      {...props}
      as={variant === 'roid' ? 'button' : 'div'}
    >
      {sizeTag !== 'circle' && (
        <>
          <Text
            variant={sizeTag === 'default' ? 'regular' : 'medium'}
            // `medium` (29-oct): la letra de `default` en una pill más baja,
            // para pills con texto largo dentro de una tabla («Vinculado con
            // Ot.X»); con la de `small` no se leía al lado del texto de la fila.
            size={sizeTag === 'small' ? 'XXXS' : 'XS'}
            // Letra según el fondo, legible (≥4,5:1): ver `textColor.ts`.
            colorText={tagTextColor(variant)}
          >
            {tag[variant]}
            {/* `fromRoute` lleva el ROID detrás igual que `campaign`. Ojo:
                <Title> pinta en NEGRO, que va bien sobre los fondos claros de
                campaign/default; sobre el garnet de `fromRoute` hay que
                heredar el blanco del <Text>. */}
            {(variant === 'campaign' || variant === 'default') && title && (
              <Title> {title}</Title>
            )}
            {variant === 'fromRoute' && title && <> {title}</>}
          </Text>
          {(variant === 'roid' || variant === 'duplicate') && <ImageIcon />}
          {actions && <ActionsContainer>{actions}</ActionsContainer>}
        </>
      )}
    </Container>
  );
};
